import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../Axios/axiosInstance";
import dayjs from "dayjs";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Snackbar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get("/api/getEmployeeRegistrations");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/deleteEmployee/${selectedEmployeeId}`);
      setDeleteMessage("Employee deleted successfully");
      setOpenSnackbar(true);
      setOpenDeleteDialog(false);
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const handleDeleteDialogOpen = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formattedDate = (date) => {
    if (!date) {
      return ""; // Return an empty string for blank dates
    }
    return dayjs(date).format("DD-MM-YYYY"); // Format the date as needed for non-blank dates
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
    setPage(0);
  };

  const navigate = useNavigate();

  return (
    <div>
      <TextField
        label="Search Employee"
        variant="outlined"
        value={searchTerm}
        onChange={handleSearch}
        style={{ marginBottom: "20px" }}
      />
      {/* Table to display employees */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Date Of Birth</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Email Id</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joining Date</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees
              .filter((employee) => {
                if (searchTerm === "") {
                  return employee;
                } else {
                  const searchTermLowerCase = searchTerm.toLowerCase();
                  return (
                    (employee.firstname
                      .toLowerCase()
                      .includes(searchTermLowerCase) ||
                      employee.lastname
                        .toLowerCase()
                        .includes(searchTermLowerCase) ||
                      dayjs(employee.dob)
                        .format("YYYY-MM-DD")
                        .includes(searchTerm) ||
                      (employee.age &&
                        employee.age.toString().includes(searchTerm)) ||
                      employee.email
                        .toLowerCase()
                        .includes(searchTermLowerCase) ||
                      employee.phoneno.includes(searchTerm) ||
                      employee.role
                        .toLowerCase()
                        .includes(searchTermLowerCase) ||
                      dayjs(employee.joiningdate)
                        .format("YYYY-MM-DD")
                        .includes(searchTerm) ||
                      employee.address
                        .toLowerCase()
                        .includes(searchTermLowerCase)) &&
                    true
                  );
                }
              })
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>{employee.firstname}</TableCell>
                  <TableCell>{employee.lastname}</TableCell>
                  <TableCell>{formattedDate(employee.dob)}</TableCell>
                  <TableCell>{employee.age}</TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>{employee.phoneno}</TableCell>

                  <TableCell>{employee.role}</TableCell>
                  <TableCell>{formattedDate(employee.joiningdate)}</TableCell>

                  <TableCell>{employee.address}</TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeleteDialogOpen(employee.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {/* Add edit functionality */}
                    <IconButton
                      aria-label="edit"
                      onClick={() =>
                        navigate(`/dashboard/employee-update/${employee.id}`)
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={employees.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this employee?
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Delete Message */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={deleteMessage}
      />
    </div>
  );
}

export default EmployeeList;
